import { Inject, Injectable } from '@angular/core';
import { ROUTER_CONFIGURATION, Router } from '@angular/router';
import moment from 'moment';
import { Register } from '../models/register';

import { RegisterService } from '../services/register.service';
import { toUniversalDateString } from '../utils/date-utils';
import {
  ROUTE_PERIOD_SEPARATOR,
  TypedActivatedRouteSnapshot,
  navigateToRouteWithReplacedParameters,
} from '../utils/route-utils';
import { PeriodData } from './period.resolver';
import { RegimeParams } from './regime.resolver';

export type FyParams = RegimeParams & {
  fyRange: string;
};

export type FyData = PeriodData;

function isResultOk(result: {
  startDate: string | null;
  endDate: string | null;
}): result is { startDate: string; endDate: string } {
  return !!result.startDate && !!result.endDate && result.startDate < result.endDate;
}

export function endDateToFY(endDate: string | null) {
  return {
    startDate:
      endDate &&
      toUniversalDateString(moment.utc(endDate).startOf('day').add(1, 'day').add(-1, 'year')),
    endDate,
  };
}

@Injectable({
  providedIn: 'root',
})
export class FyResolver {
  constructor(
    private router: Router,
    private registerService: RegisterService,
    @Inject(ROUTER_CONFIGURATION) private routerConfig: { enableTracing: boolean },
  ) {}

  async resolve(route: TypedActivatedRouteSnapshot<FyParams>) {
    const registerId: number = +route.params.registerId;

    if (!registerId) {
      throw new Error('No registerId param');
    }

    const [fromYear, toYear] = parseFyRange(route.params.fyRange || '');

    const register = await this.registerService.getRegister(registerId).toPromise();

    let endDate: string | null = null;
    if (toYear) {
      const year = parseInt(toYear, 10);
      const fyEndDetails = moment()
        .year(year)
        .month((register?.reportingYearStartDateMonth ?? 1) - 1)
        .date(register?.reportingYearStartDateDay ?? 1)
        .add(-1, 'days')
        .year(year); // Set the year again to handle Jan-Dec FY

      endDate = toUniversalDateString(fyEndDetails);
    }

    let result = endDateToFY(endDate);

    if (isResultOk(result) && result.startDate.split('-')[0] === fromYear) {
      if (this.routerConfig.enableTracing) {
        console.log('FY Resolver: resolved', result);
      }
      sessionStorage.setItem('fyEnd', result.endDate);
      sessionStorage.setItem('periodStart', result.startDate);
      sessionStorage.setItem('periodEnd', result.endDate);

      return result;
    }

    endDate = endDate || toUniversalDateString(sessionStorage.getItem('fyEnd'));
    result = endDateToFY(endDate);

    if (!isResultOk(result)) {
      const fyEndDetails = moment()
        .month((register?.reportingYearStartDateMonth ?? 7) - 1)
        .date(register?.reportingYearStartDateDay ?? 1)
        .add(-1, 'days')
        .year(moment().year()); // Set the year again to handle Jan-Dec FY

      endDate = toUniversalDateString(fyEndDetails);

      result = endDateToFY(endDate);
    }

    if (!isResultOk(result)) {
      throw new Error('Invalid FY');
    }
    if (this.routerConfig.enableTracing) {
      console.log('FY Resolver: redirecting', result);
    }

    await navigateToRouteWithReplacedParameters(
      this.router,
      {
        fyRange: createFyRange(result.startDate, result.endDate, null),
      },
      route,
    );

    return result;
  }
}

export function parseFyRange(fyRange: string): [string, string] | [null, null] {
  const regularExpression = new RegExp(
    '^(\\d{2})(\\d{2})(?:' + ROUTE_PERIOD_SEPARATOR + '(\\d{2})?(\\d{2}))?$',
  );
  const match = regularExpression.exec(fyRange);
  if (!match) {
    return [null, null];
  }
  const [, fromCentury, fromYear, toCentury, toYear] = match;
  return [fromCentury + fromYear, (toCentury || fromCentury) + (toYear || fromYear)];
}

export function createFyRange(
  start: string | null | undefined,
  end: string | null | undefined,
  register?: Register | null,
): string {
  if (!start || !end) {
    return '-';
  }
  const match = /^((\d{2})\d{2})\b/.exec(start);
  if (!match) {
    return '-';
  }
  const [, fromYear, fromCentury] = match;

  if (register) {
    // If the FY starts in Jan
    if (register.reportingYearStartDateDay === 1 && register.reportingYearStartDateMonth === 1) {
      return start;
    }

    end = (+start.split('-')[0] + 1).toString();
  } else if (+end.split('-')[0] > +start.split('-')[0] + 1) {
    end = (+start.split('-')[0] + 1).toString();
  }

  return `${start}${ROUTE_PERIOD_SEPARATOR}${end}`
    .replace(/-\d{2}/g, '')
    .replace(`${ROUTE_PERIOD_SEPARATOR}${fromYear}`, '')
    .replace(`${ROUTE_PERIOD_SEPARATOR}${fromCentury}`, ROUTE_PERIOD_SEPARATOR);
}
